import getDividendYield from "components/company/v3/utils/getDividendYield";
import getYear from "date-fns/getYear";
import isAfter from "date-fns/isAfter";
import isValid from "date-fns/isValid";
import subYears from "date-fns/subYears";
import parseDateYmd from "lib/utils/parseDateYmd";
import { InfoIcon } from "lucide-react";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import type { IDividendsPaidDetails, IDividendYieldDetails } from "types/graphqlTypes";

interface DividendsSummaryProps {
  tickerSymbol: string;
  currency: "BRL" | "USD";
  dividendsPaid: IDividendsPaidDetails[];
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
}

export default function DividendsSummary({
  tickerSymbol,
  currency,
  dividendsPaid,
  dividendYieldSnapshotEachSymbol,
}: DividendsSummaryProps) {
  const intl = useIntl();

  const summary = useMemo(() => {
    const dated = dividendsPaid
      .map((node) => ({
        node,
        // The table keys off `recordDate` (Data-Com) for Brazilian issuers and `excludingDate` (Data-Ex) elsewhere.
        referenceDate: parseDateYmd(currency === "BRL" && node.recordDate ? node.recordDate : node.excludingDate),
      }))
      .filter(({ referenceDate }) => isValid(referenceDate))
      .sort((a, b) => b.referenceDate.getTime() - a.referenceDate.getTime());

    if (dated.length === 0) {
      return null;
    }

    const twelveMonthsAgo = subYears(new Date(), 1);
    const lastTwelveMonths = dated.filter(({ referenceDate }) => isAfter(referenceDate, twelveMonthsAgo));

    return {
      latest: dated[0],
      countLastTwelveMonths: lastTwelveMonths.length,
      totalLastTwelveMonths: lastTwelveMonths.reduce((total, { node }) => total + node.amountPaid, 0),
    };
  }, [dividendsPaid, currency]);

  if (!summary) {
    return null;
  }

  const { latest, countLastTwelveMonths, totalLastTwelveMonths } = summary;

  const amount = formatAmount(intl, latest.node.amountPaid, currency);
  const referenceDate = intl.formatDate(latest.referenceDate, { year: "numeric", month: "numeric", day: "numeric" });
  const payDate = getPayableDate(latest.node.payDate);
  const formattedPayDate = payDate
    ? intl.formatDate(payDate, { year: "numeric", month: "numeric", day: "numeric" })
    : null;
  const dividendType = currency === "BRL" ? getDividendTypeLabel(intl, latest.node.dividendType) : null;

  const dividendYield =
    dividendYieldSnapshotEachSymbol.length > 0 ? getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol) : null;

  return (
    <div className="flex w-full shrink-0 items-start gap-2 self-start rounded-xl bg-slate-50/60 p-4 dark:bg-slate-800/40">
      <InfoIcon aria-hidden={true} className="mt-0.5 size-4 shrink-0 text-slate-500 dark:text-slate-400" />
      <p className="text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400">
        {renderLatestSentence({ intl, tickerSymbol, currency, amount, referenceDate, formattedPayDate, dividendType })}{" "}
        {dividendYield && dividendYield !== "-" && (
          <>
            {intl.formatMessage(
              {
                defaultMessage: "O DY dos últimos 12 meses é de {dividendYield}%.",
                id: "8XwSle",
                description: "Resumo de dividendos - Dividend Yield",
              },
              { dividendYield },
            )}{" "}
          </>
        )}
        {countLastTwelveMonths > 0 &&
          intl.formatMessage(
            {
              defaultMessage:
                "Nos últimos 12 meses {count, plural, one {foi # provento} other {foram # proventos}}, somando {total} por ação.",
              id: "akzrq4",
              description: "Resumo de dividendos - total distribuído nos últimos 12 meses",
            },
            {
              count: countLastTwelveMonths,
              total: formatAmount(intl, totalLastTwelveMonths, currency),
            },
          )}
      </p>
    </div>
  );
}

interface LatestSentenceArgs {
  intl: IntlShape;
  tickerSymbol: string;
  currency: "BRL" | "USD";
  amount: string;
  referenceDate: string;
  formattedPayDate: string | null;
  dividendType: string | null;
}

function renderLatestSentence({
  intl,
  tickerSymbol,
  currency,
  amount,
  referenceDate,
  formattedPayDate,
  dividendType,
}: LatestSentenceArgs): string {
  if (currency !== "BRL") {
    return formattedPayDate
      ? intl.formatMessage(
          {
            defaultMessage:
              "O último provento distribuído por {tickerSymbol} foi de {amount} por ação (data-ex {referenceDate}, pagamento {payDate}).",
            id: "s0t0D2",
            description: "Resumo de dividendos - último provento de uma Stock ou REIT, com data de pagamento",
          },
          { tickerSymbol, amount, referenceDate, payDate: formattedPayDate },
        )
      : intl.formatMessage(
          {
            defaultMessage:
              "O último provento distribuído por {tickerSymbol} foi de {amount} por ação (data-ex {referenceDate}).",
            id: "O0ahTJ",
            description: "Resumo de dividendos - último provento de uma Stock ou REIT, sem data de pagamento",
          },
          { tickerSymbol, amount, referenceDate },
        );
  }

  if (dividendType) {
    return formattedPayDate
      ? intl.formatMessage(
          {
            defaultMessage:
              "O último provento distribuído por {tickerSymbol} foi um {dividendType} de {amount} por ação (data-com {referenceDate}, pagamento {payDate}).",
            id: "Pb07Nc",
            description: "Resumo de dividendos - último provento com tipo e data de pagamento",
          },
          { tickerSymbol, dividendType, amount, referenceDate, payDate: formattedPayDate },
        )
      : intl.formatMessage(
          {
            defaultMessage:
              "O último provento distribuído por {tickerSymbol} foi um {dividendType} de {amount} por ação (data-com {referenceDate}).",
            id: "YSlH8t",
            description: "Resumo de dividendos - último provento com tipo, sem data de pagamento",
          },
          { tickerSymbol, dividendType, amount, referenceDate },
        );
  }

  return formattedPayDate
    ? intl.formatMessage(
        {
          defaultMessage:
            "O último provento distribuído por {tickerSymbol} foi de {amount} por ação (data-com {referenceDate}, pagamento {payDate}).",
          id: "IyUzKo",
          description: "Resumo de dividendos - último provento sem tipo, com data de pagamento",
        },
        { tickerSymbol, amount, referenceDate, payDate: formattedPayDate },
      )
    : intl.formatMessage(
        {
          defaultMessage:
            "O último provento distribuído por {tickerSymbol} foi de {amount} por ação (data-com {referenceDate}).",
          id: "QSk/j6",
          description: "Resumo de dividendos - último provento sem tipo nem data de pagamento",
        },
        { tickerSymbol, amount, referenceDate },
      );
}

function formatAmount(intl: IntlShape, amount: number, currency: "BRL" | "USD"): string {
  return intl.formatNumber(amount, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
}

function getDividendTypeLabel(intl: IntlShape, dividendType?: string | null): string | null {
  if (dividendType === "JUROS") {
    return "JCP";
  }
  if (dividendType === "DIVIDENDO") {
    return intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" });
  }
  if (dividendType === "RENDIMENTO") {
    return intl.formatMessage({ defaultMessage: "Rendimento", id: "Egt9i5" });
  }
  return null;
}

function getPayableDate(payDate?: string | null): Date | null {
  if (!payDate) {
    return null;
  }

  const parsed = parseDateYmd(payDate);
  if (!isValid(parsed)) {
    return null;
  }

  const year = getYear(parsed);
  if (year === 1900 || year >= 2500) {
    return null;
  }

  return parsed;
}
